<script setup lang="ts">
import { computed, watch, ref, onBeforeMount } from 'vue';
import i18n from '@/i18n';
import { useRouter } from 'vue-router';
import routerKey from '@/router/routerKey';
import { GQT_MARKETING } from '@/constants';
import { getEnvVar } from '@/utils/env';
import { useLayout } from '@/layout/composables/layout';
import { useGatewayStore } from '@/store/GatewayStore';
import { useDestinationStore } from '../store/DestinationStore';
import { useCruiseStore } from '../store/CruiseStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import AppTopbar from './AppTopbar.vue';
import AppSidebar from './AppSidebar.vue';
import AppConfig from './AppConfig.vue';

const { layoutConfig, layoutState, isSidebarActive } = useLayout();
const userInfo = useUserInfo();

const outsideClickListener = ref(null);

watch(isSidebarActive, (newVal) => {
  if (newVal) {
    bindOutsideClickListener();
  } else {
    unbindOutsideClickListener();
  }
});

const isHaveRoleData = ref(false);
const gatewayStore = useGatewayStore();
const destinationStore = useDestinationStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const cruiseStore = useCruiseStore();
const router = useRouter();
const airManagementStore = useAirManagementStore();
const environment = getEnvVar('VITE_SITE');

const containerClass = computed(() => {
  return {
    'layout-theme-light': layoutConfig.darkTheme.value === 'light',
    'layout-theme-dark': layoutConfig.darkTheme.value === 'dark',
    'layout-overlay': layoutConfig.menuMode.value === 'overlay',
    'layout-static': layoutConfig.menuMode.value === 'static',
    'layout-static-inactive': layoutState.staticMenuDesktopInactive.value && layoutConfig.menuMode.value === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive.value,
    'layout-mobile-active': layoutState.staticMenuMobileActive.value,
    'p-input-filled': layoutConfig.inputStyle.value === 'filled',
    'p-ripple-disabled': !layoutConfig.ripple.value
  };
});

const getUserInfo = async (currentUser: UserInfo) => {
  const userRole = currentUser?.role;

  if (userRole) {
    const authorizations: any = await userAuthorizationsStore.getListPermissionByRole(userRole);
    isHaveRoleData.value = true;
    if (userRole == GQT_MARKETING && !authorizations.travel_home.menu_option_view) {
      router.push('internal/promos');
    }
    if (userRole === GQT_MARKETING && authorizations.travel_home.menu_option_view) {
      router.push('/');
    }
  } else {
    router.push({ name: routerKey.LANDING_PAGE });
  }
};

const loadData = async () => {
  gatewayStore.getGateways();
  destinationStore.getDestinationsOnlyHotel(i18n.global.locale.value);
  cruiseStore.getCruise();
  airManagementStore.getListAir();
};

onBeforeMount(async () => {
  const currentUser = await userInfo.getUserInfo();

  if (currentUser) {
    if (environment !== 'DEV') {
      if (!currentUser?.isGuestUser) {
        loadData();
        getUserInfo(currentUser);
        return;
      }
    } else {
      loadData();
      getUserInfo(currentUser);
      return;
    }
  }

  router.push({ name: routerKey.LANDING_PAGE });
});

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive.value = false;
        layoutState.staticMenuMobileActive.value = false;
        layoutState.menuHoverActive.value = false;
      }
    };
    document.addEventListener('click', outsideClickListener.value);
  }
};

const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener);
    outsideClickListener.value = null;
  }
};

const isOutsideClicked = (event) => {
  const sidebarEl = document.querySelector('.layout-sidebar');
  const topbarEl = document.querySelector('.layout-menu-button');

  return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
};
</script>
<style lang="scss">
@use 'primeicons/primeicons.css';
@use '@/assets/demo/styles/badges.scss';
</style>
<template>
  <LoadingSpinner v-if="!isHaveRoleData" />
  <div v-if="isHaveRoleData" class="layout-wrapper" :class="containerClass">
    <app-topbar></app-topbar>

    <div class="layout-main-container">
      <div class="layout-sidebar">
        <app-sidebar></app-sidebar>
      </div>
      <div class="layout-main">
        <router-view></router-view>
      </div>
    </div>
    <app-config :simple="true"></app-config>
  </div>
</template>
